/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    NOTIFICATIONS_SPLIT_SANITIZER
 * Date:         2025-12-30
 * Modified By:  Cursor
 *
 * Description:
 * Extracted error sanitization logic from notifications.ts.
 */

import axios from "axios";

export function sanitizeTechnicalTerms(message: string): string {
  let sanitized = message;
  const technicalTerms = [
    /mongo/i,
    /mongoose/i,
    /schema/i,
    /validationError/i,
    /path\s+`[^`]+`/gi,
    /at\s+.*\.js:\d+:\d+/gi,
    /Error:\s*/gi,
    /\[object\s+Object\]/gi,
    /undefined/gi,
    /null/gi,
  ];

  technicalTerms.forEach((term) => {
    sanitized = sanitized.replace(term, "");
  });

  return sanitized.trim().replace(/\s+/g, " ");
}

export function sanitizeServerError(error: any): string | null {
  if (axios.isAxiosError(error) && error.response) {
    const status = error.response.status;
    const data = error.response.data || {};

    if (data.code === "ACCOUNT_SUSPENDED" || data.error?.code === "ACCOUNT_SUSPENDED") {
      return "Your account has been suspended. Please contact support for assistance.";
    }

    const errorMessage = data.message || data.error || "";
    if (typeof errorMessage === "string" && errorMessage.toLowerCase().includes("suspended")) {
      return "Your account has been suspended. Please contact support for assistance.";
    }

    switch (status) {
      case 400: {
        // [2026-01-30] broadcast-errors: Friendly messaging for 400 payload shapes
        // Profile completeness middleware shape:
        // { error: "PROFILE_INCOMPLETE", message, missingFields: string[], profileUrl }
        if (data?.error === "PROFILE_INCOMPLETE") {
          const missing = Array.isArray(data?.missingFields)
            ? data.missingFields.filter((x: any) => typeof x === "string" && x.trim().length > 0)
            : [];
          const missingText = missing.length > 0 ? `Missing: ${missing.join(", ")}.` : null;
          const nextStep = "Please update your profile and try again.";
          const base = typeof data?.message === "string" && data.message.trim().length > 0
            ? sanitizeTechnicalTerms(data.message)
            : "Please complete your profile before sending broadcasts.";
          return [base, missingText, nextStep].filter(Boolean).join(" ");
        }

        // validate.js schema middleware shape:
        // { success:false, error:{ message:"Validation failed", details:[...] } }
        const details = Array.isArray(data?.error?.details) ? data.error.details : null;
        if (data?.success === false && details && details.length > 0) {
          const lines = details
            .filter((d: any) => typeof d === "string" && d.trim().length > 0)
            .map((d: string) => sanitizeTechnicalTerms(d))
            .slice(0, 5);
          if (lines.length > 0) {
            const suffix = details.length > lines.length ? " (and more)" : "";
            return `Please check your input: ${lines.join("; ")}${suffix}.`;
          }
          return "Please check your input and try again.";
        }

        // Generic backend error shape (used by several controllers):
        // { errors: [{ path, message }, ...] }
        if (Array.isArray(data?.errors) && data.errors.length > 0) {
          const messages = data.errors
            .map((e: any) => e?.message)
            .filter((m: any) => typeof m === "string" && m.trim().length > 0)
            .map((m: string) => sanitizeTechnicalTerms(m));
          if (messages.length > 0) return messages.join(". ");
        }

        if (typeof data?.message === "string" && data.message.trim().length > 0) {
          return sanitizeTechnicalTerms(data.message);
        }

        // Last resort for 400s
        return "Please review the form and try again.";
      }
      case 401: return "Your session has expired. Please sign in again.";
      case 403: return "You do not have permission to perform this action.";
      case 404: return "Requested item was not found.";
      case 409: return "This action conflicts with existing data. Please refresh and try again.";
      case 413: return "File is too large. Please choose a smaller file.";
      case 422:
        if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          const messages = data.errors
            .map((e: any) => e.message)
            .filter((msg: any) => msg && typeof msg === "string")
            .map((msg: string) => sanitizeTechnicalTerms(msg));
          if (messages.length > 0) return messages.join(". ");
        }
        return "Please check your input and try again.";
      case 429: {
        // QUOTA_EXCEEDED (BR-11) returns a structured payload from the broadcast send endpoint
        const code = data?.error || data?.code;
        if (code === "QUOTA_EXCEEDED") {
          const limit = typeof data?.limit === "number" ? data.limit : 5;
          const blocked = Array.isArray(data?.blockedCategories)
            ? data.blockedCategories
            : (data?.exceededCategory ? [data.exceededCategory] : []);

          const breakdown = Array.isArray(data?.categories) && data.categories.length > 0
            ? data.categories
                .map((c: any) => {
                  const cat = c?.category || "Unknown";
                  const cur = typeof c?.current === "number" ? c.current : "?";
                  const lim = typeof c?.limit === "number" ? c.limit : limit;
                  const blockedFlag = c?.isBlocked ? " (blocked)" : "";
                  return `${cat} ${cur}/${lim}${blockedFlag}`;
                })
                .join(", ")
            : null;

          const resetAtUtc =
            typeof data?.resetAtUtc === "string" ? data.resetAtUtc : null;
          const resetText = resetAtUtc
            ? `after ${new Date(resetAtUtc).toUTCString()}`
            : "after the daily reset (00:00 UTC)";

          // [2026-01-30] broadcast-errors: UI copy requested by designer (Title + Body)
          const title = "Daily limit reached";
          const body = "You’ve reached the daily broadcast limit for this category.";

          const exceededText =
            blocked.length > 0
              ? `Exceeded categories: ${blocked.join(", ")}.`
              : "Exceeded categories: one or more categories.";

          const countsText = breakdown ? `Counts today: ${breakdown}.` : null;
          const nextSteps =
            blocked.length > 0
              ? `Remove items in exceeded categories or try again ${resetText}.`
              : `Try again ${resetText}.`;

          return [title, body, exceededText, countsText, nextSteps]
            .filter(Boolean)
            .join("\n");
        }

        if (data?.code === "RATE_LIMITED" && typeof data?.retryAfterSeconds === "number") {
          return `Too many requests. Please try again in ${data.retryAfterSeconds} seconds.`;
        }

        return "Too many requests. Please wait a moment and try again.";
      }
      case 500:
      case 502:
      case 503:
      case 504:
        return "Something went wrong on our side. Please try again.";
      default:
        if (data.message && typeof data.message === "string") return sanitizeTechnicalTerms(data.message);
        if (data.error?.message && typeof data.error.message === "string") return sanitizeTechnicalTerms(data.error.message);
        return null;
    }
  }

  if (axios.isAxiosError(error) && error.code === "ECONNABORTED") {
    return "Request timed out. Please check your connection and try again.";
  }

  if (axios.isAxiosError(error) && !error.response) {
    return "Cannot connect to server. Please check your connection.";
  }

  if (error instanceof Error && error.message) {
    return sanitizeTechnicalTerms(error.message);
  }

  if (typeof error === "string") {
    return sanitizeTechnicalTerms(error);
  }

  return null;
}
